// src/app/core/shared/simulation-api.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { map } from 'rxjs/operators';

type OutputFromCsvResponse = {
  city: string;
  country: string;
  run_id: string;
  years: string[];
  htn: Record<string, Record<string, number>>;
  cm: Record<string, Record<string, number>>;
};

type Row = {
  year: number;
  aware_of_screening_knowledge_male: number;
  aware_of_screening_knowledge_female: number;
  population_30_plus_normal_bp_male: number;
  population_30_plus_normal_bp_female: number;
  screened_practice_male: number;
  screened_practice_female: number;
  cvd_death_total: number;
  cvd_death_male: number;
  cvd_death_female: number;
  cvd_event_rate: number;
  cvd_events_total: number;
  cvd_events_male: number;
  cvd_events_female: number;
  cvd_patients_total: number;
  cvd_patients_male: number;
  cvd_patients_female: number;
  prevalence_total: number;
  diagnosed_total: number;
  diagnosed_male: number;
  diagnosed_female: number;
  percent_diagnosed: number;
  total_prehypertension_male: number;
  total_prehypertension_female: number;
  undiagnosed_total: number;
  undiagnosed_male: number;
  undiagnosed_female: number;
  percent_undiagnosed: number;
  treated_total: number;
  treated_male: number;
  treated_female: number;
  percent_treated: number;
  controlled_total?: number;
  percent_controlled: number;
  controlled_male: number;
  controlled_female: number;
  treated_not_controlled_male: number;
  treated_not_controlled_female: number;
};

@Injectable({ providedIn: 'root' })
export class SimulationApiService {
  constructor(private http: HttpClient) { }

  private makeSessionId(): string {
    const t = Date.now();
    return `11111111-1111-1111-1111-${(t.toString().padStart(12, '0')).slice(-12)}`;
  }

  loadInitialFromFile(path = 'assets/initial-response.json'): Observable<OutputFromCsvResponse> {
    return this.http.get<OutputFromCsvResponse>(path);
  }

  startRun(payload: any): Observable<OutputFromCsvResponse> {
    const url = `${environment.apiUrl}/simulation`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      // 'x-session-id': this.makeSessionId()
    });

    return this.http
      .post(url, payload, {
        headers,
        responseType: 'text' // backend returns Base64 string, not JSON directly
      })
      .pipe(
        map((base64: string) => {
          try {
            // 1) Decode Base64
            const jsonString = atob(base64);

            // 2) Parse JSON
            const parsed = JSON.parse(jsonString);

            return parsed as OutputFromCsvResponse;
          } catch (e) {
            console.error('Failed to decode/parse simulation response:', e, 'raw:', base64);
            // Fallback: return an empty structure to avoid crashing callers
            return {
              city: '',
              country: '',
              run_id: '',
              years: [],
              htn: {},
              cm: {}
            } as OutputFromCsvResponse;
          }
        })
      );
  }

  mapToRows(resp: any): { rows: Row[] } {
    const years: string[] = Array.isArray(resp?.years) ? resp.years : [];
    const htn = resp?.htn || {};

    // Robust numeric parser that tolerates junk like "****34.66"
    const num = (k: string, y: string): number => {
      try {
        const raw = htn?.[k]?.[y];

        if (raw === null || raw === undefined) {
          return 0; // default
        }

        // Convert to string and remove commas
        let s = String(raw).replace(/,/g, '');

        // Remove any characters that are not digit, dot, or minus sign
        s = s.replace(/[^0-9.\-]/g, '');

        if (!s) {
          return 0;
        }

        const v = Number(s);
        return Number.isFinite(v) ? v : 0;
      } catch {
        // Absolute fallback: never throw from here
        return 0;
      }
    };

    const rows: Row[] = years.map((y) => ({
      year: Number(y),
      aware_of_screening_knowledge_male: num('Aware_of_Screening_Male', y),
      aware_of_screening_knowledge_female: num('Aware_of_Screening_Female', y),
      population_30_plus_normal_bp_male: num('Normal_BP_Male', y),
      population_30_plus_normal_bp_female: num('Normal_BP_Female', y),
      screened_practice_male: num('Screened_Male', y),
      screened_practice_female: num('Screened_Female', y),
      cvd_death_total: num('CVD_Death', y),
      cvd_death_male: num('CVD_Death_Male', y),
      cvd_death_female: num('CVD_Death_Female', y),
      cvd_event_rate: 0,
      cvd_events_total: num('CVD_Events', y),
      cvd_events_male: num('CVD_Events_Male', y),
      cvd_events_female: num('CVD_Events_Female', y),
      cvd_patients_total: num('CVD_patients', y),
      cvd_patients_male: num('CVD_Patients_Male', y),
      cvd_patients_female: num('CVD_Patients_Female', y),
      prevalence_total: num('Prevalence_Male', y) + num('Prevalence_Female', y),
      diagnosed_total: num('Diagnosed', y),
      diagnosed_male: num('Diagnosed_Male', y),
      diagnosed_female: num('Diagnosed_Female', y),
      percent_diagnosed: 0,
      total_prehypertension_male: num('Prehypertension_Male', y),
      total_prehypertension_female: num('Prehypertension_Female', y),
      undiagnosed_total: num('Undiagnosed', y),
      undiagnosed_male: num('Undiagnosed_Male', y),
      undiagnosed_female: num('Undiagnosed_Female', y),
      percent_undiagnosed: 0,
      treated_total: num('Treated', y),
      treated_male: num('Treated_Male', y),
      treated_female: num('Treated_Female', y),
      percent_treated: 0,
      controlled_total: htn?.Controlled?.[y] !== undefined ? num('Controlled', y) : undefined,
      percent_controlled: 0,
      controlled_male: num('Controlled_Male', y),
      controlled_female: num('Controlled_Female', y),
      treated_not_controlled_male: num('Treated_Not_Controlled_Male', y),
      treated_not_controlled_female: num('Treated_Not_Controlled_Female', y),
    }));

    return { rows };
  }
}